import { useMemo, useState } from 'react'
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons'
import styles from './Prompt.module.less'
import { sendAnalyticsTrack } from 'Utils/analytics.utils'
import {
    ContextType,
    MediaTypeDefinitions,
    PromptDataType,
    SingleValueDataType,
    TaskAdditionalInfo,
    UseCaseTypes,
    ValueType,
    ValueTypeDefinitions,
} from 'Types/Tasks.types'
import { PromptHTMLContent } from 'Components/tasks/Prompt/PromptContentTypes/PromptHTMLContent'
import { PromptImageContent } from 'Components/tasks/Prompt/PromptContentTypes/PromptImageContent'
import { PromptAudioContent } from 'Components/tasks/Prompt/PromptContentTypes/PromptAudioContent'
import { PromptVideoContent } from 'Components/tasks/Prompt/PromptContentTypes/PromptVideoContent'
import { PromptMultiMediaContent } from 'Components/tasks/Prompt/PromptContentTypes/PromptMultiMediaContent'
import { PromptHTMLHighlightableContent } from 'Components/tasks/Prompt/PromptContentTypes/PromptHTMLHighlightableContent.tsx'
import { PromptStringContent } from 'Components/tasks/Prompt/PromptContentTypes/PromptStringContent'
import { PromptMarkdownContent } from 'Components/tasks/Prompt/PromptContentTypes/PromptMarkdownContent'

type PromptProps = {
    title: string
    content?: {
        value_type: ValueType
        value: PromptDataType
    }
    taskInfo: TaskAdditionalInfo
    contextType?: ContextType
}

type PromptContentPropsType = {
    type?: ValueType
    value?: PromptDataType
    useCase: UseCaseTypes
    id: string
}

export const PromptContent = (props: PromptContentPropsType) => {
    const id = useMemo(() => {
        return props.id + '-value'
    }, [props.id])

    switch (props.type) {
        case 'HTML':
            return props.useCase ===
                UseCaseTypes.SFT_GENERATION_RESPONSE_GENERATION_SUMMARISATION_USECASE ? (
                <PromptHTMLHighlightableContent content={props.value as string} id={id} />
            ) : (
                <PromptHTMLContent content={props.value as string} id={id} />
            )
        case 'STRING':
            return <PromptStringContent content={props.value as string} id={id} />
        case 'MARKDOWN':
            return <PromptMarkdownContent content={props.value as string} id={id} />
        case 'IMAGE':
            return <PromptImageContent content={props.value as MediaTypeDefinitions} id={id} />
        case 'AUDIO':
            return <PromptAudioContent content={props.value as MediaTypeDefinitions} id={id} />
        case 'VIDEO':
            return <PromptVideoContent content={props.value as MediaTypeDefinitions} id={id} />
        case 'MULTI_MEDIA_GRID':
            return (
                <PromptMultiMediaContent
                    content={props.value as SingleValueDataType<keyof ValueTypeDefinitions>[][]}
                    useCase={props.useCase}
                    id={id}
                />
            )
    }
}

const Prompt: React.FC<PromptProps> = props => {
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false)

    const handlePromptDropdownClick = () => {
        setIsCollapsed(!isCollapsed)
        sendAnalyticsTrack(isCollapsed ? 'Prompt_Opened' : 'Prompt_Closed', {
            task_id: props.taskInfo.taskId,
            project_id: props.taskInfo.projectId,
            user_id: props.taskInfo.assignedUserId,
            step_id: props.taskInfo.stepId,
        })
    }

    return (
        <div
            className={`${styles['prompt']} ${
                props.taskInfo.useCase !==
                    UseCaseTypes.SFT_GENERATION_RESPONSE_GENERATION_RATING_USECASE &&
                props.contextType !== ContextType.ExamTask
                    ? styles['prompt-container-max-height']
                    : ''
            }`}
            data-testid="prompt-container"
        >
            <button className={styles['prompt-heading']} onClick={handlePromptDropdownClick}>
                <span className={styles['prompt-heading-icon']}>
                    {isCollapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
                </span>
                <span>{props.title.toUpperCase()}</span>
            </button>
            {!isCollapsed && (
                <div
                    className={styles['prompt-content']}
                    style={{ marginTop: props.content ? '8px' : '0' }}
                >
                    <PromptContent
                        type={props.content?.value_type}
                        value={props.content?.value}
                        useCase={props.taskInfo?.useCase}
                        id="content"
                    />
                </div>
            )}
        </div>
    )
}

export default Prompt
